<script lang="ts">
  import Button from '$lib/components/ui/button/button.svelte';

  import { type SingleStamp, type ReviewState, type WithoutIdAndReview, canReview, canEdit } from '@briefmarken_katalog/types';

  import DateOfIssue from './DateOfIssue.svelte';
  import Title from './Title.svelte';
  import FaceValue from './FaceValue.svelte';
  import FieldSeparator from './FieldSeparator.svelte';
  import Picture from '../Picture.svelte';
  import StampEditor from '../editors/StampEditor.svelte';
  import ReviewStateIcon from './ReviewStateIcon.svelte';
  import RowActionButtons from './RowActionButtons.svelte';
  import CatalogDetailDialog from './CatalogDetailDialog.svelte';

  import type { Snippet } from 'svelte';
  import { user, doLogin } from "$lib/stores/auth";
  import { catalogMode } from "$lib/stores/ui";

  import { StampEditorModel } from "../editors/models/StampEditorModel.svelte"
  import { reviewSuggestion, getItem, getSuggestion, deleteSuggestion } from "$lib/services/philatelic_items";
  import { t } from "$lib/i18n";
  import { applyReviewResult } from "./applyReviewResult";

  import './row.css'

  const { stamp, readonly = false, leading }: { stamp: SingleStamp; readonly?: boolean; leading?: Snippet } = $props<{stamp: SingleStamp; readonly?: boolean; leading?: Snippet}>();

  let isViewerOpen: boolean = $state(false);
  let editorModel = $state<StampEditorModel | null>(null);
  let catalogDetailItem = $state<SingleStamp | null>(null);
  let isCatalogDetailOpen = $state(false);

  async function markAsInReview() {
    try {
      applyReviewResult(stamp, await reviewSuggestion(stamp._id!, 'InReview', undefined, doLogin));
    } catch (e: any) {
      alert($t('errors.markAsInReview') + e.message);
    }
  }

  async function markAsSuggested() {
    try {
      applyReviewResult(stamp, await reviewSuggestion(stamp._id!, 'Suggested', undefined, doLogin));
    } catch (e: any) {
      alert($t('errors.markAsInReview') + e.message);
    }
  }

  async function fetchFull(): Promise<SingleStamp | null> {
    const fetch = stamp.review.state === 'Accepted' ? getItem : getSuggestion;
    return await fetch(stamp._id!, doLogin) as SingleStamp | null;
  }

  function suggestedBySuffix(full: SingleStamp): string {
    return full.review.suggestedByName
      ? ` ${$t('editor.suggestedBy')} ${full.review.suggestedByName}`
      : '';
  }

  async function openReview() {
    const full = await fetchFull();
    if (!full) return;
    editorModel = new StampEditorModel({
      isReview: true,
      initialValue: full,
      dialogTitle: 'editor.stampReviewTitle',
      dialogSubtitle: 'editor.stampReviewSubtitle',
      onCreate: async (updatedStamp: WithoutIdAndReview<SingleStamp>) => {
        try {
          applyReviewResult(stamp, await reviewSuggestion(stamp._id!, stamp.review.state, updatedStamp, doLogin));
        } catch (e: any) {
          alert($t('errors.reviewFailed') + e.message);
        }
      },
      onReview: async (updatedStamp: WithoutIdAndReview<SingleStamp>, state: ReviewState) => {
        try {
          applyReviewResult(stamp, await reviewSuggestion(stamp._id!, state, updatedStamp, doLogin));
        } catch (e: any) {
          alert($t('errors.reviewFailed') + e.message);
        }
      }
    });
    editorModel.dialogSubtitleSuffix = suggestedBySuffix(full);
    isViewerOpen = true;
  }

  async function openDelete() {
    const full = await fetchFull();
    if (!full) return;
    editorModel = new StampEditorModel({
      isDelete: true,
      initialValue: full,
      dialogTitle: 'deleteSuggestion.title',
      dialogSubtitle: 'deleteSuggestion.subtitle',
      onDelete: async () => {
        try {
          await deleteSuggestion(stamp._id!, doLogin);
        } catch (e: any) {
          alert($t('errors.deleteSuggestion') + e.message);
        }
      }
    });
    editorModel.dialogSubtitleSuffix = suggestedBySuffix(full);
    isViewerOpen = true;
  }

  async function openDetails(ro: boolean) {
    const full = await fetchFull();
    if (!full) return;
    // Catalog mode gets the presentation-style detail dialog instead of the editor
    if (ro && $catalogMode) {
      catalogDetailItem = full;
      isCatalogDetailOpen = true;
      return;
    }
    const subtitleKey = stamp.review.state !== 'Accepted'
      ? 'editor.stampDetailsSuggestionSubtitle' as const
      : 'editor.stampDetailsCatalogSubtitle' as const;
    editorModel = new StampEditorModel({
      isReadonly: ro,
      isEdit: !ro,
      initialValue: full,
      dialogTitle: 'editor.stampDetailsTitle',
      dialogSubtitle: subtitleKey,
      ...(!ro && {
        onCreate: async (updatedStamp: WithoutIdAndReview<SingleStamp>) => {
          try {
            applyReviewResult(stamp, await reviewSuggestion(stamp._id!, stamp.review.state, updatedStamp, doLogin));
          } catch (e: any) {
            alert($t('errors.reviewFailed') + e.message);
          }
        },
      }),
    });
    if (stamp.review.state !== 'Accepted') editorModel.dialogSubtitleSuffix = suggestedBySuffix(full);
    isViewerOpen = true;
  }
</script>

<!--
  Desktop (>= sm): one horizontal flex line; the group wrappers dissolve via
  sm:contents and the sm:order-* wrappers restore the original field order.
  Mobile (< sm): two stacked lines — picture + title, then date / face value / actions.
-->
<div class="row max-sm:flex-col max-sm:gap-1" style="
  {stamp.review.state === 'Rejected' ? 'background-color: #fecaca;' : ''}
">
  {@render leading?.()}
  <!-- mobile line 1: picture + title -->
  <div class="max-sm:flex max-sm:items-start max-sm:gap-1 max-sm:w-full sm:contents">
    <div class="shrink-0 sm:order-3">
      <Picture picture={stamp.pictures ? stamp.pictures[0] : undefined} fetchFullImage={() => fetchFull().then(f => f?.pictures?.[0])} />
    </div>
    <div class="min-w-0 flex-1 sm:order-7">
      <Title text={stamp.title}/>
    </div>
  </div>
  <!-- mobile line 2: date · face value · actions -->
  <div class="max-sm:flex max-sm:items-center max-sm:w-full sm:contents">
    <div class="sm:order-1">
      <DateOfIssue bind:date={stamp.date_of_issue}/>
    </div>
    <div class="sm:order-5">
      <FaceValue data={stamp.face_value}/>
    </div>
    {#if !readonly}
      <div class="sm:order-9">
        <ReviewStateIcon
          state={stamp.review.state}
          reviewedBy={stamp.review.reviewedBy}
          currentUserId={$user?.id}
          userCanReview={!!$user && canReview($user.role)}
          onMarkAsInReview={markAsInReview}
          onDropReview={markAsSuggested}
        />
      </div>
    {/if}
    <div class="max-sm:ml-auto sm:order-11">
      <RowActionButtons
        {readonly}
        state={stamp.review.state}
        reviewedBy={stamp.review.reviewedBy}
        currentUserId={$user?.id}
        userCanReview={!!$user && canReview($user.role)}
        userCanEdit={!!$user && canEdit(stamp, $user.id)}
        userIsAdmin={$user?.role === 'Admin'}
        userIsLoggedIn={!!$user}
        onReview={openReview}
        onDelete={openDelete}
        onDetails={openDetails}
      />
    </div>
  </div>
  <!-- desktop-only vertical dividers, slotted between the fields via sm:order-* -->
  <FieldSeparator class="sm:order-2"/>
  <FieldSeparator class="sm:order-4"/>
  <FieldSeparator class="sm:order-6"/>
  <FieldSeparator class="sm:order-8"/>
  {#if !readonly}
    <FieldSeparator class="sm:order-10"/>
  {/if}
</div>

{#if isViewerOpen && editorModel}
<StampEditor bind:model={editorModel!} bind:isOpen={isViewerOpen}></StampEditor>
{/if}

{#if isCatalogDetailOpen && catalogDetailItem}
<CatalogDetailDialog item={catalogDetailItem} bind:isOpen={isCatalogDetailOpen} />
{/if}
